import * as React from 'react';
import * as _ from 'lodash';
import { MobileMenuHeader } from './MobileMenuHeader';
import { MobileMenuSlider } from './MobileMenuSlider';
import { IMobileMenuProps, IMobileMenuState, MenuItem, MenuBreadcrumb } from './MobileMenu.types';

/**
 * Mobile menu with breadcrumbs
 */
export default class MobileMenu extends React.Component<IMobileMenuProps, IMobileMenuState> {
  constructor(props: IMobileMenuProps) {
    super(props);

    let currentPageIsStartPage = this.props.isStartPage;
    let treeNodesClone = _.cloneDeep(this.props.treeNodes);

    this.buildParentRefs(treeNodesClone, { PageName: [], PageUrl: [] });
    let currentPageMenuItem = this.findDeepMenuItem(treeNodesClone, this.props.currentPageId);

    // If currentPageMenuItem is null we're probably on a page that is hidden in navigation. Thus we simply show the Startpage as meny root.
    if (currentPageMenuItem === null) {
      currentPageMenuItem = treeNodesClone;
      currentPageIsStartPage = true;
    }

    // Leaf node in the tree, get the parent instead
    if (!currentPageMenuItem.HasChildren && this.props.highlightCurrentPage) {
      currentPageMenuItem.IsCurrentPage = true;
      currentPageMenuItem = this.findDeepMenuItem(treeNodesClone, currentPageMenuItem.ParentId);
      currentPageIsStartPage = currentPageMenuItem.PageTreeDepth === 1 ? true : false;
    }

    this.state = {
      activeMenuItem: currentPageMenuItem,
      isStartPage: currentPageIsStartPage,
      tree: treeNodesClone,
      isNavigatingBackwards: false,
    };
  }

  static defaultProps = {
    isCampaignPage: false,
  };

  buildParentRefs = (menuItem: MenuItem, Breadcrumb: MenuBreadcrumb): void => {
    if (!menuItem) return;
    menuItem.Breadcrumb = Breadcrumb;

    let breadcrumb = _.cloneDeep(Breadcrumb);

    breadcrumb.PageName.push(menuItem.Name);
    breadcrumb.PageUrl.push(menuItem.Url);

    let bc: MenuBreadcrumb = {
      PageName: breadcrumb.PageName,
      PageUrl: breadcrumb.PageUrl,
    };

    if (menuItem.Children && menuItem.Children.length) {
      menuItem.Children.forEach((item: MenuItem) => {
        this.buildParentRefs(item, bc);
      });
    }
  };

  findDeepMenuItem = (menuItem: MenuItem, id: number): MenuItem => {
    if (!menuItem) return null;

    if (menuItem.hasOwnProperty('Id') && menuItem['Id'] == id) return menuItem;

    for (var i = 0; i < Object.keys(menuItem).length; i++) {
      if (typeof menuItem[Object.keys(menuItem)[i]] == 'object') {
        let o = this.findDeepMenuItem(menuItem[Object.keys(menuItem)[i]], id);
        if (o != null) return o;
      }
    }
    return null;
  };

  addChildrenToTree = (children: MenuItem[], id: number): void => {
    // Create a clone of the tree that we can work with
    let treeClone = _.cloneDeep(this.state.tree);
    // Find the MenuItem that we're going to update
    let menuItemClone = this.findDeepMenuItem(treeClone, id);
    if (menuItemClone) {
      // Update the MenuItem with its children
      menuItemClone.Children = children;
      this.buildParentRefs(treeClone, { PageName: [], PageUrl: [] });
      // Update tree state with the new tree
      this.setState(() => {
        return {
          tree: treeClone,
        };
      });
    }
  };

  clickPreviousLevel = (): void => {
    let previousMenuItem = this.findDeepMenuItem(this.state.tree, this.state.activeMenuItem.ParentId);
    this.setActiveMenuItem(previousMenuItem, true);
  };

  clickHome = (): void => {
    this.setActiveMenuItem(this.state.tree, true);
  };

  setActiveMenuItem = (menuItem: MenuItem, isNavigatingBackwards: boolean): void => {
    this.setState(() => {
      return {
        activeMenuItem: menuItem,
        isNavigatingBackwards: isNavigatingBackwards,
        isStartPage: menuItem !== null ? menuItem.Id === this.props.startPageId : true,
      };
    });
  };

  render() {
    return (
      <nav
        className="mobile-menu"
        aria-live="polite"
        aria-label={
          this.props.language == 'en'
            ? 'Hierarchical navigation menu for the entire website'
            : 'Hierarkisk navigationsmeny för hela webbplatsen'
        }>
        {(!this.props.hideHeaderOnStartPage || this.state.activeMenuItem.PageTreeDepth !== 1) && (
          <MobileMenuHeader
            homeTriggeredCallback={this.clickHome}
            previousLevelTriggeredCallback={this.clickPreviousLevel}
            isStartPage={this.state.isStartPage}
            activeMenuItem={this.state.activeMenuItem}
            isCampaignPage={this.props.isCampaignPage}
            ariaLabelBackInMenu={this.props.ariaLabelBackInMenu}
          />
        )}
        <MobileMenuSlider
          language={this.props.language}
          activeMenuItem={this.state.activeMenuItem}
          isNavigatingBackwards={this.state.isNavigatingBackwards}
          setActiveMenuItemCallback={this.setActiveMenuItem}
          addChildrenToTreeCallback={this.addChildrenToTree}
          isStartPage={this.state.isStartPage}
        />
      </nav>
    );
  }
}
